import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { GlassCard } from '../components/GlassCard';
import { Receipt, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function stepMonth(ym, dir) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + dir, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

export default function ErpGST() {
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney, taxLabel, countryCode } = useTenantFormat();
  const isGCC = countryCode !== 'IN';

  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hsnOpen, setHsnOpen] = useState(false);
  const [hsnSort, setHsnSort] = useState({ key: 'total_value', dir: 'desc' });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = isGCC
        ? await api.erpVAT(token, { month })
        : await api.erpGST(token, { month });
      setData(r);
    } catch (e) {
      if (e.status === 403) return;
      toast.show(e.message || 'Failed to load tax report', 'error');
    } finally { setLoading(false); }
  }, [token, month, toast, isGCC]);

  useEffect(() => { load(); }, [load]);

  const toggleHsnSort = (key) => {
    setHsnSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const sortedHsn = data?.hsn_summary ? [...data.hsn_summary].sort((a, b) => {
    const av = a[hsnSort.key], bv = b[hsnSort.key];
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return hsnSort.dir === 'asc' ? cmp : -cmp;
  }) : [];

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="erp-gst-page">
      {/* GST/VAT info banner */}
      {isGCC && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/25" data-testid="erp-vat-banner">
          <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
          <span className="text-amber-400 text-xs font-semibold">Showing VAT report for {countryCode}. Input/Output VAT follows single-rate regime.</span>
        </div>
      )}

      {/* Month stepper */}
      <div className="flex items-center gap-3">
        <button onClick={() => setMonth(m => stepMonth(m, -1))} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors" data-testid="erp-gst-prev">
          <ChevronLeft size={18} />
        </button>
        <span className="text-white font-bold text-lg min-w-[180px] text-center" data-testid="erp-gst-month">{monthLabel(month)}</span>
        <button onClick={() => setMonth(m => stepMonth(m, 1))} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors" data-testid="erp-gst-next">
          <ChevronRight size={18} />
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* 4 KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Total Sales" value={data.total_sales_rupees} color="#10B981" testId="erp-gst-kpi-sales" formatMoney={formatMoney} />
            <KpiCard label={`Output ${taxLabel}`} value={data.output_gst_rupees} color="#F59E0B" testId="erp-gst-kpi-output" formatMoney={formatMoney} />
            <KpiCard label="Total Purchases" value={data.total_purchases_rupees} color="#EF4444" testId="erp-gst-kpi-purchases" formatMoney={formatMoney} />
            <KpiCard label={`Input ${taxLabel}`} value={data.input_gst_rupees} color="#14B8A6" testId="erp-gst-kpi-input" formatMoney={formatMoney} />
          </div>

          {/* Net GST Payable */}
          <GlassCard dark className="p-5" testId="erp-gst-kpi-net">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">Net {taxLabel} Payable</p>
            <p className={`font-extrabold text-3xl tabular-nums mt-1 ${data.net_gst_payable_rupees > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {formatMoney(data.net_gst_payable_rupees)}
            </p>
            <p className="text-white/30 text-xs mt-1">{data.net_gst_payable_rupees > 0 ? 'Amount owed to government' : 'Credit / Refund due'}</p>
          </GlassCard>

          {/* CGST/SGST/IGST breakdown */}
          <div className="flex flex-wrap gap-2">
            {data.output_gst_breakdown && (
              <>
                <Chip label="Output CGST" value={data.output_gst_breakdown.cgst} formatMoney={formatMoney} />
                <Chip label="Output SGST" value={data.output_gst_breakdown.sgst} formatMoney={formatMoney} />
                <Chip label="Output IGST" value={data.output_gst_breakdown.igst} formatMoney={formatMoney} />
              </>
            )}
            {data.input_gst_breakdown && (
              <>
                <Chip label="Input CGST" value={data.input_gst_breakdown.cgst} color="teal" formatMoney={formatMoney} />
                <Chip label="Input SGST" value={data.input_gst_breakdown.sgst} color="teal" formatMoney={formatMoney} />
                <Chip label="Input IGST" value={data.input_gst_breakdown.igst} color="teal" formatMoney={formatMoney} />
              </>
            )}
          </div>

          {/* HSN Summary */}
          <div>
            <button
              onClick={() => setHsnOpen(v => !v)}
              data-testid="erp-gst-hsn-toggle"
              className="flex items-center gap-2 text-white/50 text-xs font-bold uppercase tracking-wider hover:text-white/80 transition-colors mb-3"
            >
              {hsnOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {hsnOpen ? 'Hide' : 'Show'} HSN Summary ({sortedHsn.length})
            </button>
            {hsnOpen && (
              <GlassCard dark className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white/[0.04] border-b border-white/10">
                        {[
                          { key: 'hsn_code', label: 'HSN Code' },
                          { key: 'description', label: 'Description' },
                          { key: 'quantity', label: 'Qty', align: 'right' },
                          { key: 'total_value', label: 'Total Value', align: 'right' },
                          { key: 'cgst', label: 'CGST', align: 'right' },
                          { key: 'sgst', label: 'SGST', align: 'right' },
                          { key: 'igst', label: 'IGST', align: 'right' },
                          { key: 'total_gst', label: 'Total GST', align: 'right' },
                        ].map(col => (
                          <th
                            key={col.key}
                            onClick={() => toggleHsnSort(col.key)}
                            className={`px-4 py-3 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/70 select-none whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'} text-white/50`}
                          >
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              {hsnSort.key === col.key && (hsnSort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHsn.map((h, i) => (
                        <tr key={h.hsn_code} data-testid={`erp-gst-hsn-row-${h.hsn_code}`} className={`border-b border-white/[0.04] hover:bg-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                          <td className="px-4 py-3 text-white font-bold text-xs tabular-nums">{h.hsn_code}</td>
                          <td className="px-4 py-3 text-white/70 text-xs">{h.description}</td>
                          <td className="px-4 py-3 text-right text-white/60 text-xs tabular-nums">{h.quantity}</td>
                          <td className="px-4 py-3 text-right text-white text-xs tabular-nums font-bold">{formatMoney(h.total_value)}</td>
                          <td className="px-4 py-3 text-right text-white/60 text-xs tabular-nums">{formatMoney(h.cgst)}</td>
                          <td className="px-4 py-3 text-right text-white/60 text-xs tabular-nums">{formatMoney(h.sgst)}</td>
                          <td className="px-4 py-3 text-right text-white/60 text-xs tabular-nums">{formatMoney(h.igst)}</td>
                          <td className="px-4 py-3 text-right text-white font-bold text-xs tabular-nums">{formatMoney(h.total_gst)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function KpiCard({ label, value, color, testId, formatMoney }) {
  return (
    <GlassCard dark className="p-4" testId={testId}>
      <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{label}</p>
      <p className="text-white font-extrabold text-lg tabular-nums mt-1" style={color ? { color } : undefined}>{formatMoney(value)}</p>
    </GlassCard>
  );
}

function Chip({ label, value, color = 'amber', formatMoney: fmt }) {
  const f = fmt || ((v) => String(v));
  const cls = color === 'teal'
    ? 'bg-teal-500/10 text-teal-400 border-teal-500/20'
    : 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border tabular-nums ${cls}`}>
      {label}: {f(value)}
    </span>
  );
}


